import { OrgSwitcherItem } from '@pure/pure1-ui-platform-angular';

import { BootstrapOrgContext, EffectiveOrg } from '../data/services/effective-org.service';
import { extractOrgUidFromPath } from '../data/utils/org-url';
import { pickEffectiveOrg } from '../data/utils/pick-effective-org';
import { sortOrgsByName } from '../data/utils/sort-orgs-by-name';
import { ORG_URL_FF } from './guards/org-url-shared';

/** The parts of window.dxp (UI Platform init.js) read before Angular bootstraps. */
type BootstrapDxp = {
    ready?: Promise<unknown>;
    featureFlag?: { get(names: string): Promise<Record<string, { enabled: boolean } | undefined>> };
    getOrgs?: () => Promise<OrgSwitcherItem[]>;
    getEffectiveOrg?: () => Omit<EffectiveOrg, 'opCompanyName'> | undefined;
    introspect?: { ph_ocid?: string };
};

const UNRESOLVED_ORG_CONTEXT: BootstrapOrgContext = { orgSwitchUrlEnabled: null, effectiveOrg: null };

/**
 * Resolves the effective org before Angular bootstraps, from what the UI Platform has already
 * loaded: the eiam-7-org-switch-url flag and the org list are cached by window.dxp, and the home
 * operating company is the ph_ocid claim, so this issues no request of its own.
 *
 * Never throws: whatever cannot be resolved is left null and primeEffectiveOrg resolves it in
 * APP_INITIALIZER instead.
 */
export async function resolveBootstrapOrgContext(win: Window): Promise<BootstrapOrgContext> {
    try {
        // index.html loads init.js asynchronously and, until it runs, window.dxp is only a stub holding
        // `config` and `ready`. init.js then replaces window.dxp with the platform (keeping the stub's
        // `ready`), so wait for `ready` first and only then read the platform APIs.
        await (win as any).dxp.ready;
        const dxp: BootstrapDxp | undefined = (win as any).dxp;
        if (!dxp?.featureFlag || !dxp.getOrgs) {
            return UNRESOLVED_ORG_CONTEXT;
        }
        // The org list is loaded regardless of the flag: the app needs it either way (org switcher).
        const [flags, orgs] = await Promise.all([dxp.featureFlag.get(ORG_URL_FF), dxp.getOrgs()]);
        const orgSwitchUrlEnabled = flags[ORG_URL_FF]?.enabled ?? false;

        if (!orgSwitchUrlEnabled) {
            const platformOrg = dxp.getEffectiveOrg?.();
            return { orgSwitchUrlEnabled, effectiveOrg: platformOrg ? { ...platformOrg, opCompanyName: null } : null };
        }
        const org = pickEffectiveOrg(
            sortOrgsByName(orgs),
            extractOrgUidFromPath(win.location.pathname),
            dxp.introspect?.ph_ocid,
        );
        return { orgSwitchUrlEnabled, effectiveOrg: org ? toEffectiveOrg(org) : null };
    } catch (error) {
        console.error('Failed to resolve the org context before bootstrap', error);
        return UNRESOLVED_ORG_CONTEXT;
    }
}

export function toEffectiveOrg(item: OrgSwitcherItem): EffectiveOrg {
    return {
        id: item.id,
        uid: item.uid,
        name: item.name,
        category: item.category as string,
        opCompanyUid: item.opCompany.uid,
        opCompanyName: item.opCompany.name,
    };
}
